import { Component, OnInit } from '@angular/core';
import { UserService } from '../services/user.service';
import { DatabaseService } from '../services/database.service';
import { AlertService } from '../services/alert.service';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { EventMetricsDialogComponent } from '../event-metrics-dialog/event-metrics-dialog.component';

@Component({
  selector: 'app-my-requests',
  templateUrl: './my-requests.component.html',
  styleUrls: ['./my-requests.component.scss'],
  standalone: false
})
export class MyRequestsComponent implements OnInit {
  myRequests: any[] = [];
  filteredRequests: any[] = [];
  loading: boolean = true;
  user: any;
  activeFilter: string = 'all'; // Track the current filter

  constructor(
    private _user: UserService,
    private _db: DatabaseService,
    private _alert: AlertService,
    private router: Router,
    private location: Location,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    // Get current user and load their requests
    this._user.user$.subscribe((user) => {
    //   console.log('My Requests - User subscription received:', user);
      if (user) {
        this.user = user;
        this.loadMyRequests();
      } else {
        console.log('My Requests - No user available yet');
        this.loading = false;
      }
    });
  }

  loadMyRequests(): void {
    this.loading = true;
    
    // Get all events from the database and filter by current user
    this._db.getAllEvents().subscribe({
      next: (response) => {
        // console.log('My Requests - Raw response:', response);
        
        // Handle different response formats
        let events = response;
        if (events && typeof events === 'object') {
          events = events.events || events;
        }
        
        //  console.log('My Requests - Processed events:', events);
        
        if (Array.isArray(events) && this.user) {
          // Filter events for current user
          const userEmail = (this.user.mail || this.user.userPrincipalName || '').toLowerCase();
          const userGmid = this.user.userPrincipalName?.split('@')[0]?.toLowerCase();
          const userName = (this.user.displayName || this.user.name || '').toLowerCase();
          
          // Create alternative email formats to handle domain differences
          const userEmailAlternatives = [
            userEmail,
            this.user.mail,
            this.user.userPrincipalName,
            userGmid ? `${userGmid}@nam.corp.gm.com` : '',
            userGmid ? `${userGmid}@namqa.corpqa.geuc.corp.gm.com` : '',
            userName ? userName.replace(/\s+/g, '.') + '@gm.com' : '',
            userName ? userName.replace(/\s+/g, '.') + '@pp.gm.com' : '',
            userEmail ? userEmail?.split('@')[0]?.toLowerCase() + '@gm.com' : '',
            userEmail ? userEmail?.split('@')[0]?.toLowerCase() + '@pp.gm.com' : ''
          ].filter(email => email).map(email => email.toLowerCase());
          
          console.log('My Requests - Filtering for user:', { 
            userEmail, 
            userGmid, 
            userName,
            userEmailAlternatives,
            fullUser: this.user 
          });
          
          const myEvents = events.filter((event: any) => {
            // Get all possible requestor/creator fields
            const requestorFields = this.getPersonFields(event.requestor);
            const leaderFields = this.getPersonFields(event.event_leader);
            const eventRequestor = (requestorFields.email || requestorFields.name || requestorFields.gmid || '').toLowerCase();
            const eventLeader = (leaderFields.email || leaderFields.name || leaderFields.gmid || '').toLowerCase();
            const eventRequestorGmid = (requestorFields.gmid || '').toLowerCase();
            const eventLeaderGmid = (leaderFields.gmid || '').toLowerCase();
            const eventRequestorName = (requestorFields.name || '').toLowerCase();
            const eventLeaderName = (leaderFields.name || '').toLowerCase();
            const eventEmail = (event.requester_email || event.email || requestorFields.email || '').toLowerCase();
            const eventCreator = (event.creator || '').toLowerCase();
            const eventOwner = (event.owner || '').toLowerCase();
            const eventSubmitter = (event.submitter || '').toLowerCase();
            const eventAssignedTo = (event.assigned_to || '').toLowerCase();
            
            // Check if any of the user's email alternatives match any event field
            const isMyEvent = userEmailAlternatives.some(userEmailAlt => 
              eventRequestor === userEmailAlt ||
              eventLeader === userEmailAlt ||
              eventEmail === userEmailAlt ||
              eventCreator === userEmailAlt ||
              eventOwner === userEmailAlt ||
              eventSubmitter === userEmailAlt ||
              eventAssignedTo === userEmailAlt ||
              eventRequestorGmid === userEmailAlt ||
              eventLeaderGmid === userEmailAlt ||
              eventRequestorName === userEmailAlt ||
              eventLeaderName === userEmailAlt ||
              // Also check if the user email/GMID is contained in the event fields
              eventRequestor.includes(userEmailAlt) ||
              eventLeader.includes(userEmailAlt) ||
              eventEmail.includes(userEmailAlt) ||
              eventCreator.includes(userEmailAlt) ||
              eventAssignedTo.includes(userEmailAlt) ||
              eventRequestorGmid.includes(userEmailAlt) ||
              eventLeaderGmid.includes(userEmailAlt) ||
              eventRequestorName.includes(userEmailAlt) ||
              eventLeaderName.includes(userEmailAlt)
            ) || 
            // Additional check for GMID matches
            (userGmid && (
              eventRequestor.includes(userGmid) ||
              eventLeader.includes(userGmid) ||
              eventRequestorGmid.includes(userGmid) ||
              eventLeaderGmid.includes(userGmid) ||
              eventEmail.includes(userGmid) ||
              eventCreator.includes(userGmid) ||
              eventAssignedTo.includes(userGmid)
            )) ||
            // Name-based matching (more flexible)
            (userName && (
              eventRequestor.includes(userName) ||
              eventLeader.includes(userName) ||
              eventRequestorName.includes(userName) ||
              eventLeaderName.includes(userName) ||
              eventCreator.includes(userName) ||
              eventAssignedTo.includes(userName)
            ));
            
            // console.log(`My Requests - Event "${event.request_title || event.title}":`, {
            //   eventRequestor,
            //   eventLeader, 
            //   eventEmail,
            //   eventCreator,
            //   eventOwner,
            //   eventSubmitter,
            //   eventAssignedTo,
            //   isMyEvent,
            //   matchedBy: userEmailAlternatives.filter(alt => 
            //     eventRequestor === alt || eventLeader === alt || eventEmail === alt || 
            //     eventCreator === alt || eventOwner === alt || eventSubmitter === alt ||
            //     eventAssignedTo === alt ||
            //     eventRequestor.includes(alt) || eventLeader.includes(alt) || eventEmail.includes(alt) ||
            //     eventAssignedTo.includes(alt)
            //   )
            // });
            
            return isMyEvent;
          });
          
          console.log('My Requests - My events:', myEvents);
          console.log('My Requests - Total events before filtering:', events.length);
          console.log('My Requests - My events after filtering:', myEvents.length);
          
          // If no events found, let's also log a sample of all events to see the structure
        //   if (myEvents.length === 0 && events.length > 0) {
        //     console.log('My Requests - No events found for user. Sample event structure:', events[0]);
        //     console.log('My Requests - All events for debugging:', events.map(e => ({
        //       title: e.request_title || e.title,
        //       requestor: e.requestor,
        //       event_leader: e.event_leader,
        //       creator: e.creator,
        //       requester_email: e.requester_email
        //     })));
        //   }
          
          const todayMidnight = new Date();
          todayMidnight.setHours(0,0,0,0);

          this.myRequests = myEvents.map((event: any) => {
            const start = event.start_date ? new Date(event.start_date) : undefined;
            let dateCategory: 'past' | 'today' | 'future' = 'future';
            if (!start || isNaN(start.getTime())) {
              dateCategory = 'past'; // push invalid dates to the bottom
            } else {
              const startDay = new Date(start);
              startDay.setHours(0,0,0,0);
              if (startDay.getTime() === todayMidnight.getTime()) dateCategory = 'today';
              else if (startDay.getTime() < todayMidnight.getTime()) dateCategory = 'past';
              else dateCategory = 'future';
            }
            return {
              ...event,
              dateCategory,
              displayTitle: event.request_title || event.title || 'Untitled Event',
              displayDate: this.formatDate(event.start_date),
              displayTime: this.formatTime(event.start_date, event.end_date),
              requestor: event.requestor?.name || event.requestor?.email || event.requestor || 'Unknown',
              status: event.request_status || 'Unsubmitted',
              location: event.primary_presenting_location || 'Not specified'
            };
          }).sort((a, b) => {
            // Ordering: today (earliest time first), upcoming future (earliest first), then past (most recent past first)
            const rank = (cat: string) => cat === 'today' ? 0 : (cat === 'future' ? 1 : 2);
            const rankDiff = rank(a.dateCategory) - rank(b.dateCategory);
            if (rankDiff !== 0) return rankDiff;
            const timeA = new Date(a.start_date || 0).getTime();
            const timeB = new Date(b.start_date || 0).getTime();
            if (a.dateCategory === 'past') {
              // Most recent past first (descending)
              return timeB - timeA;
            }
            // Today & future: earliest first (ascending)
            return timeA - timeB;
          });
          
          // Initialize filtered requests with all requests
          this.applyFilter(this.activeFilter);
          
        //   console.log('My Requests - Final processed requests:', this.myRequests);
        } else {
          console.log('My Requests - No events array found or no user');
          this.myRequests = [];
          this.filteredRequests = [];
        }
        
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading my requests:', error);
        this.myRequests = [];
        this.filteredRequests = [];
        this.loading = false;
      }
    });
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'No date';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid date';
      
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  }

  formatTime(startDate: string, endDate: string): string {
    if (!startDate) return 'No time';
    
    try {
      const start = new Date(startDate);
      if (isNaN(start.getTime())) return 'Invalid time';
      
      const startTime = start.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      
      if (endDate) {
        const end = new Date(endDate);
        if (!isNaN(end.getTime())) {
          const endTime = end.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          });
          return `${startTime} - ${endTime}`;
        }
      }
      
      return startTime;
    } catch (error) {
      console.error('Error formatting time:', error);
      return 'Invalid time';
    }
  }

  private getPersonFields(person: any): { email: string; gmid: string; name: string } {
    if (!person) return { email: '', gmid: '', name: '' };
    if (typeof person === 'string') {
      const trimmed = person.trim();
      if (!trimmed) return { email: '', gmid: '', name: '' };
      return {
        email: trimmed.includes('@') ? trimmed.toLowerCase() : '',
        gmid: '',
        name: trimmed
      };
    }
    return {
      email: (person.email || '').toLowerCase(),
      gmid: (person.gmid || '').toLowerCase(),
      name: (person.name || person.displayName || '').toLowerCase()
    };
  }

  getStatusClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'status-approved'
      case 'completed':
        return 'status-completed';
      case 'unsubmitted':
        return 'status-unsubmitted';
      case 'pending':
        return 'status-pending';
      case 'rejected':
      case 'cancelled':
      case 'canceled':
        return 'status-canceled';
      case 'in progress':
      case 'processing':
        return 'status-in-progress';
      default:
        return 'status-default';
    }
  }

  editRequest(event: any): void {
    if (this.deleteStopEdit) {
      this.deleteStopEdit = false;
      return;
    }
    console.log('Editing event:', event, " with ID:", event.event_id);
    // Routes to #/request?id=event_id
    const tree = this.router.createUrlTree(['/request'], { queryParams: { id: event.event_id } });
    const url = this.location.prepareExternalUrl(this.router.serializeUrl(tree));
    window.open(url, '_blank');
  }

  deleteStopEdit = false;
  async deleteRequest(request: any) {
    const confirmed = await this._alert.confirm(`Are you sure you want to delete "${request.displayTitle}"? This action cannot be undone.`);
  
    if (confirmed) {
      this.deleteStopEdit = true;
      this._db.deleteEventById(request.event_id).subscribe({
        next: (res: any) => {
          console.log('Request deleted:', res);
        },
        error: (error) => {
          console.error('Error deleting request:', error);
        }
      });
      this.myRequests = this.myRequests.filter(r => r.event_id !== request.event_id);
      // Update filtered requests as well
      this.applyFilter(this.activeFilter);
    }
  }

  // Refresh requests data
  refreshRequests(): void {
    this.loadMyRequests();
  }

  getUnsubmittedCount(): number {
    return this.myRequests.filter(request => 
      request.status?.toLowerCase() === 'unsubmitted'
    ).length;
  }

  getPendingCount(): number {
    return this.myRequests.filter(request => 
      request.status?.toLowerCase() === 'pending'
    ).length;
  }

  getApprovedCount(): number {
    return this.myRequests.filter(request => 
      request.status?.toLowerCase() === 'approved'
    ).length;
  }

  getCompletedCount(): number {
    return this.myRequests.filter(request =>
      request.status?.toLowerCase() === 'completed'
    ).length;
  }

  getCanceledCount(): number {
    return this.myRequests.filter(request => 
      request.status?.toLowerCase() === 'rejected' || 
      request.status?.toLowerCase() === 'cancelled' ||
      request.status?.toLowerCase() === 'canceled'
    ).length;
  }

  trackByRequestId(index: number, request: any): any {
    return request.id || request.event_id || index;
  }

  // Check if request is in the future
  isFutureRequest(request: any): boolean {
    if (!request.start_date) return false;
    const requestDate = new Date(request.start_date);
    const now = new Date();
    return requestDate > now;
  }

  // Check if request is happening today
  isRequestToday(request: any): boolean {
    if (!request.start_date) return false;
    
    const today = new Date();
    const requestDate = new Date(request.start_date);
    
    return today.toDateString() === requestDate.toDateString();
  }

  // Get relative time description (e.g., "Today", "Tomorrow", "In 3 days")
  getRelativeTimeDescription(request: any): string {
    if (!request.start_date) return '';
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const requestDate = new Date(request.start_date);
    requestDate.setHours(0, 0, 0, 0);
    
    const diffTime = requestDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays === -1) return 'Yesterday';
    if (diffDays > 1) return `In ${diffDays} days`;
    if (diffDays < -1) return `${Math.abs(diffDays)} days ago`;
    
    return '';
  }

  // Filtering methods
  applyFilter(filterType: string): void {
    this.activeFilter = filterType;
    
    switch (filterType) {
      case 'unsubmitted':
        this.filteredRequests = this.myRequests.filter(request => 
          request.status?.toLowerCase() === 'unsubmitted'
        );
        break;
      case 'pending':
        this.filteredRequests = this.myRequests.filter(request => 
          request.status?.toLowerCase() === 'pending'
        );
        break;
      case 'approved':
        this.filteredRequests = this.myRequests.filter(request => 
          request.status?.toLowerCase() === 'approved'
        );
        break;
      case 'completed':
        this.filteredRequests = this.myRequests.filter(request => 
          request.status?.toLowerCase() === 'completed'
        );
        break;
      case 'canceled':
        this.filteredRequests = this.myRequests.filter(request => 
          request.status?.toLowerCase() === 'rejected' || 
          request.status?.toLowerCase() === 'cancelled' ||
          request.status?.toLowerCase() === 'canceled'
        );
        break;
      case 'all':
      default:
        this.filteredRequests = [...this.myRequests];
        break;
    }
  }

  // Click handlers for filter cards
  filterByUnsubmitted(): void {
    this.applyFilter('unsubmitted');
  }

  filterByPending(): void {
    this.applyFilter('pending');
  }

  filterByApproved(): void {
    this.applyFilter('approved');
  }

  filterByCompleted(): void {
    this.applyFilter('completed');
  }

  filterByCanceled(): void {
    this.applyFilter('canceled');
  }

  filterByAll(): void {
    this.applyFilter('all');
  }

  // Check if a filter is active
  isFilterActive(filterType: string): boolean {
    return this.activeFilter === filterType;
  }

  // Get display-friendly status text
  getDisplayStatus(status: string): string {
    if (!status) return 'Unknown';
    
    const statusLower = status.toLowerCase();
    if (statusLower === 'rejected' || statusLower === 'cancelled' || statusLower === 'canceled') {
      return 'Canceled';
    }
    
    // Capitalize first letter of other statuses
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  }

  // Delete event with confirmation
  async deleteEvent(request: any, event?: Event): Promise<void> {
    // Prevent event bubbling to avoid opening the request
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (!this.user?.isAdmin && !this.user?.isOwner) {
      await this._alert.error('You do not have permission to delete events.');
      return;
    }

    if (!request.event_id) {
      await this._alert.error('Cannot delete event: Event ID not found.');
      return;
    }

    const confirmed = await this._alert.confirm(
      `Are you sure you want to delete this event?\n\nTitle: ${request.displayTitle || request.request_title || 'Untitled'}\nID: #${request.event_id}\n\nThis action cannot be undone.`,
      'Delete Event'
    );

    if (confirmed) {
      this._db.deleteEventById(request.event_id).subscribe({
        next: async (response) => {
          await this._alert.success('Event deleted successfully.');
          this.loadMyRequests(); // Reload the list
        },
        error: async (error) => {
          console.error('Error deleting event:', error);
          await this._alert.error('Failed to delete event. Please try again.', this._alert.getErrorDetails(error));
        }
      });
    }
  }

  hasMetrics(request: any): boolean {
    return (
      Number(request?.gm_video_attendees) > 0 ||
      Number(request?.teams_attendees) > 0 ||
      Number(request?.virtual_events_attendees) > 0 ||
      Number(request?.venue_attendees) > 0
    );
  }

  openMetricsDialog(request: any, clickEvent: Event): void {
    clickEvent.stopPropagation();
    clickEvent.preventDefault();
    this.dialog.open(EventMetricsDialogComponent, {
      width: '520px',
      maxWidth: '95vw',
      data: {
        eventTitle: request.displayTitle || request.request_title || 'Untitled Event',
        eventId: request.event_id,
        gm_video_attendees: request.gm_video_attendees || 0,
        teams_attendees: request.teams_attendees || 0,
        virtual_events_attendees: request.virtual_events_attendees || 0,
        venue_attendees: request.venue_attendees || 0,
      }
    });
  }
}
